import { Client } from "@genesys/gc-mfe-auth";

// Use the same app name format as our API headers
const APP_NAME = `quality-forms-webui`;

/**
 * AuthClientService wraps the gc-mfe-auth Client to provide token management
 * for the MRMO (Multi-Region Multi-Org) project.
 *
 * This service:
 * - Gets fresh tokens for every API call (no caching)
 * - Notifies the host app when a 401 response occurs
 * - Supports token change callbacks for future token rotation
 */
class AuthClientService {
    private client: Client | null = null;

    private getClient(): Client {
        if (!this.client) {
            // Lazy initialization - only create Client when first needed
            this.client = new Client(APP_NAME);
        }
        return this.client;
    }

    /**
     * Get the current authentication token from gc-mfe-auth.
     * This reads from sessionStorage 'gcui_auth' which is set by the parent window in embedded mode.
     *
     * @returns Promise<string> The current bearer token
     */
    async getToken(): Promise<string> {
        return this.getClient().getToken();
    }

    /**
     * Notify the host application that a 401 response was received.
     * The host app will validate the token and take corrective action if needed.
     *
     * @param expiredToken The token that was used in the failing request
     */
    notify401Response(expiredToken: string): void {
        const client = this.getClient();
        client.notify401Response(expiredToken);
    }

    /**
     * Register a callback to be notified when the token changes.
     * This is for future-proofing against token rotation during a user session.
     *
     * @param callback Function to call when token changes
     */
    onTokenChange(callback: (newToken: string) => void): void {
        const client = this.getClient();
        client.onTokenChange(callback);
    }
}

// Export a singleton instance
const authClientService = new AuthClientService();

export default authClientService;
